import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EditIconComponent,
  LockIconComponent,
  ViewIconComponent,
  EyeHiddenIconComponent,
} from '@kuiper/icons';
import {
  passwordValidation,
  regexVariables,
} from '../../../../common/constants/regex.constant';
import { InputComponent } from '../../../../../ui-components/lib/input/input.component';
import { ValidationMessageComponent } from '../../../../../ui-components/lib/validation-message/validation-message.component';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { AuthService } from '../../../../common/services/auth/auth.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { LogoutHelperService } from '../../../../common/helpers/logout-helper/logout-helper.service';

@Component({
  selector: 'lib-security-tab',
  imports: [
    TranslocoPipe,
    RouterModule,
    ReactiveFormsModule,
    InputComponent,
    ValidationMessageComponent,
    EditIconComponent,
    LockIconComponent,
    ViewIconComponent,
    EyeHiddenIconComponent,
  ],
  templateUrl: './security-tab.component.html',
  styleUrl: './security-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SecurityTabComponent {
  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  readonly modalService = inject(ModalServiceService);
  readonly tenantService = inject(TenantService);
  readonly destroyRef = inject(DestroyRef);
  readonly logoutHelper = inject(LogoutHelperService);

  showCurrentPassword = false;
  showPassword = false;
  showConfirmationPassword = false;
  changePasswordForm = signal<FormGroup>(new FormGroup({}));
  changePasswordModal = viewChild<TemplateRef<unknown>>('changePasswordModal');
  isChangeButtonDisabled = signal(false);

  passwordValidationStatus = {
    length: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
    touched: false,
  };

  constructor() {
    this.initChangePasswordForm();
  }

  initChangePasswordForm() {
    this.changePasswordForm.set(
      new FormGroup(
        {
          current_password: new FormControl('', [Validators.required]),
          new_password: new FormControl('', [
            Validators.required,
            Validators.pattern(regexVariables.password),
          ]),
          new_password_confirmation: new FormControl('', Validators.required),
        },
        {
          validators: this.authService.matchingPasswords(
            'new_password',
            'new_password_confirmation'
          ),
        }
      )
    );

    const passwordControl = this.changePasswordForm().get('new_password');

    passwordControl?.valueChanges.subscribe(new_password => {
      this.validatePassword(new_password);
      this.passwordValidationStatus.touched = passwordControl.touched;
    });
  }

  validatePassword(new_password: string) {
    if (new_password) {
      this.passwordValidationStatus = {
        ...this.passwordValidationStatus,
        length: new_password.length >= passwordValidation.minLength,
        upperCase: passwordValidation.hasUpperCase.test(new_password),
        lowerCase: passwordValidation.hasLowerCase.test(new_password),
        number: passwordValidation.hasNumber.test(new_password),
        specialChar: passwordValidation.hasSpecialChar.test(new_password),
      };
    }
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.changePasswordModal(),
      width: '27.375',
      canNotCloseFromOutside: true,
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
    this.changePasswordForm().reset();
    this.passwordValidationStatus = {
      length: false,
      upperCase: false,
      lowerCase: false,
      number: false,
      specialChar: false,
      touched: false,
    };
  }

  changePassword() {
    this.isChangeButtonDisabled.set(true);
    this.authService.changePassword(this.changePasswordForm().value)
      .pipe(finalize(() => {
        this.logoutHelper.handleLogout();
        this.isChangeButtonDisabled.set(false);
      }), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeModal();
        },
        error: (error: any) => {
          this.addControlErrors(error);
        },
      });
  }

  addControlErrors(error: { errors: Record<string, string[]> }) {
    const errors = error.errors;
    for (const key in errors) {
      const control = this.changePasswordForm().get(key);
      // Set the error message for the form control
      if (control) {
        control.setErrors({
          apiError: true,
          apiErrorMessage: errors[key][0],
        });
      }
    }
  }
}
